import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Search, Loader2 } from "lucide-react";
import { Footer } from "@/components/site/Footer";
import { SearchSuggestions, useSuggestions } from "@/components/site/SearchSuggestions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (src) => (src && src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src || "");

const TopBar = () => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/" data-testid="search-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
        <span className="text-[#E30613]">VL</span><span className="text-white">MAG</span>
      </Link>
      <Link to="/" data-testid="search-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors">
        <ArrowLeft size={16} /> Back To VLMAG
      </Link>
    </div>
  </header>
);

export default function StoriesSearch() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const suggestions = useSuggestions(input, focused && input !== q);

  useEffect(() => {
    setInput(q);
    setLoading(true);
    document.title = q ? `"${q}" — Search VLMAG` : "Stories — VLMAG";
    axios.get(`${API}/search`, { params: { q } }).then((r) => setResults(r.data)).catch(() => setResults([])).finally(() => setLoading(false));
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    const v = input.trim();
    setParams(v ? { q: v } : {});
  };

  return (
    <div className="bg-black text-white min-h-screen" data-testid="stories-search-page">
      <TopBar />
      <section className="px-5 md:px-10 pt-[108px] md:pt-[128px] pb-20 md:pb-28">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">Stories</p>
        <h1 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-7xl">
          {q ? <>Results for <span className="text-[#E30613]">“{q}”</span></> : "Search VLMAG"}
        </h1>

        <div className="relative max-w-2xl">
        <form onSubmit={submit} className="mt-8 flex border border-white/20 focus-within:border-[#E30613] transition-colors" data-testid="search-form">
          <Search size={18} className="self-center ml-4 text-neutral-500" />
          <input
            data-testid="search-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 120)}
            placeholder="Search artists, designers, features…"
            autoFocus
            className="flex-1 bg-transparent px-4 py-4 text-base outline-none placeholder:text-neutral-600"
          />
          <button type="submit" data-testid="search-submit" className="px-6 text-xs uppercase tracking-[0.2em] font-bold bg-[#E30613] hover:bg-white hover:text-black transition-colors">Search</button>
        </form>
        {focused && input !== q && (
          <SearchSuggestions data={suggestions} q={input} onStory={(s) => navigate(`/story/${s.id}`)} onTerm={(t) => { setInput(t); setParams({ q: t }); setFocused(false); }} className="absolute left-0 right-0 top-full mt-2 z-20" />
        )}
        </div>

        {loading ? (
          <div className="mt-16 flex items-center gap-3 text-neutral-500" data-testid="search-loading"><Loader2 className="animate-spin" size={18} /> Searching…</div>
        ) : results.length === 0 ? (
          <div className="mt-16 border border-white/10 py-20 flex flex-col items-center text-center px-6" data-testid="search-empty">
            <Search size={30} className="text-neutral-700 mb-5" />
            <p className="text-neutral-400 text-lg font-bold">No stories match “{q}”</p>
            <p className="text-neutral-600 text-sm mt-2 max-w-sm">Try an artist name, a designer, a network like “VLMAG MUSIC”, or a theme like “streetwear”.</p>
          </div>
        ) : (
          <>
            <p className="mt-10 text-xs uppercase tracking-[0.2em] font-bold text-neutral-500" data-testid="search-count">{results.length} {results.length === 1 ? "story" : "stories"}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-6" data-testid="search-grid">
              {results.map((s, i) => (
                <motion.article
                  key={s.id}
                  data-testid={`search-result-${s.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                  className="group border border-white/10 hover:border-[#E30613] transition-colors duration-500"
                >
                  <Link to={`/story/${s.id}`} className="block">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" />
                      <span className="absolute top-0 left-0 bg-black px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613] border-r border-b border-white/10">{s.network}</span>
                    </div>
                    <div className="p-5 md:p-6">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{s.category}</span>
                      <h3 className="font-display uppercase text-xl md:text-2xl leading-[0.95] tracking-tight text-white group-hover:text-[#E30613] transition-colors duration-300 mt-3">{s.title}</h3>
                      {s.excerpt && <p className="text-sm text-neutral-400 mt-3 leading-relaxed line-clamp-2">{s.excerpt}</p>}
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
